import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Sparkles, Calendar, Package, FileText, Container, DollarSign,
  ArrowUpRight, ChevronDown
} from 'lucide-react';
import { motion } from 'framer-motion';
import { getTodayForInput, getMonthsAgoForInput } from '../../utils/dateUtils';
import { baseURL } from '@/config/APIConfig';

const BACKEND_URL = baseURL.AiBaseURL;

const accent = {
  emerald: {
    light: {
      iconBg: 'bg-emerald-50', iconText: 'text-emerald-600', iconRing: 'ring-emerald-100',
      trendBg: 'bg-emerald-50', trendText: 'text-emerald-700',
      bar: 'bg-emerald-500',
    },
    dark: {
      iconBg: 'bg-emerald-500/10', iconText: 'text-emerald-400', iconRing: 'ring-emerald-500/20',
      trendBg: 'bg-emerald-500/10', trendText: 'text-emerald-400',
      bar: 'bg-emerald-500',
    },
    beam: 'rgba(16,185,129,0.72)',
  },
  amber: {
    light: {
      iconBg: 'bg-amber-50', iconText: 'text-amber-600', iconRing: 'ring-amber-100',
      trendBg: 'bg-amber-50', trendText: 'text-amber-700',
      bar: 'bg-amber-500',
    },
    dark: {
      iconBg: 'bg-amber-500/10', iconText: 'text-amber-400', iconRing: 'ring-amber-500/20',
      trendBg: 'bg-amber-500/10', trendText: 'text-amber-400',
      bar: 'bg-amber-500',
    },
    beam: 'rgba(245,158,11,0.72)',
  },
  violet: {
    light: {
      iconBg: 'bg-violet-50', iconText: 'text-violet-600', iconRing: 'ring-violet-100',
      trendBg: 'bg-violet-50', trendText: 'text-violet-700',
      bar: 'bg-violet-500',
    },
    dark: {
      iconBg: 'bg-violet-950/20', iconText: 'text-violet-400', iconRing: 'ring-violet-500/20',
      trendBg: 'bg-violet-500/10', trendText: 'text-violet-400',
      bar: 'bg-violet-500',
    },
    beam: 'rgba(139,92,246,0.72)',
  },
  blue: {
    light: {
      iconBg: 'bg-blue-50', iconText: 'text-blue-600', iconRing: 'ring-blue-100',
      trendBg: 'bg-blue-50', trendText: 'text-blue-700',
      bar: 'bg-blue-500',
    },
    dark: {
      iconBg: 'bg-blue-500/10', iconText: 'text-blue-400', iconRing: 'ring-blue-500/20',
      trendBg: 'bg-blue-500/10', trendText: 'text-blue-400',
      bar: 'bg-blue-500',
    },
    beam: 'rgba(59,130,246,0.72)',
  },
};

const KpiCard = ({ icon: Icon, label, value, sub, trend, accentKey }) => {
  const a = accent[accentKey];

  return (
    <div className="group relative overflow-hidden rounded-xl p-[1px] transition-all duration-200 ease-out hover:shadow-lg hover:-translate-y-0.5 dark:hover:shadow-slate-800/50">
      {/* Rotating beam with increased width */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-[-300%] animate-[spin_7s_linear_infinite] bg-[conic-gradient(from_0deg,transparent_0deg,transparent_260deg,var(--beam-color)_280deg,transparent_360deg)]"
        style={{ '--beam-color': a.beam }}
      />
      <span aria-hidden className="pointer-events-none absolute inset-[1px] rounded-[11px] bg-white dark:bg-slate-900" />
      <div className="relative z-10 rounded-[11px] border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
        <span className={`absolute inset-x-0 top-0 h-[3px] ${a.light.bar} dark:${a.dark.bar} opacity-0 group-hover:opacity-100 transition-opacity`} />

        <div className="flex items-center justify-between">
          {/* Icon with visible border */}
          <div className={`w-11 h-11 rounded-xl flex items-center justify-center border-2 ${a.light.iconRing.replace('ring-', 'border-')} ${a.light.iconBg} ${a.light.iconText} dark:${a.dark.iconBg} dark:${a.dark.iconText} dark:${a.dark.iconRing.replace('ring-', 'border-')}`}>
            <Icon className="w-5 h-5" strokeWidth={2.2} />
          </div>
          <span className={`inline-flex items-center gap-1 px-2 h-6 rounded-full text-[11px] font-semibold ${a.light.trendBg} ${a.light.trendText} dark:${a.dark.trendBg} dark:${a.dark.trendText}`}>
            <ArrowUpRight className="w-3 h-3" strokeWidth={2.5} />
            {trend}
          </span>
        </div>

        <div className="mt-4 text-[28px] font-bold text-slate-900 dark:text-white tabular-nums leading-none tracking-tight">
          {value}
        </div>
        <div className="mt-1.5 text-[13px] font-semibold text-slate-800 dark:text-slate-200">{label}</div>
        <div className="mt-1 text-[11.5px] text-slate-500 dark:text-slate-400">
          {trend} vs last month · {sub}
        </div>
      </div>
    </div>
  );
};

const AIOverviewPanel = ({ onAISummaryClick }) => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [realMetrics, setRealMetrics] = useState(null);
  const [fromDate, setFromDate] = useState(getMonthsAgoForInput(3));
  const [toDate, setToDate] = useState(getTodayForInput());
  const [authUser] = JSON.parse(localStorage.getItem("authUser") ?? "[{}]");
  const companyId = authUser?.companyId;

  useEffect(() => {
    console.log('AIOverviewPanel: Starting fetch...');
    fetchRealMetrics();
    fetchSummary();
  }, []);

  const fetchSummary = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/ai/summary`);
      setSummary(response.data);
    } catch (error) {
      console.error('Error fetching AI summary:', error);
    }
  };

  const fetchRealMetrics = async () => {
    try {
      console.log('Fetching real metrics from:', `${BACKEND_URL}/api/dashboard/real-metrics`);
      const response = await axios.get(`${BACKEND_URL}/api/dashboard/real-metrics`);
      console.log('Real metrics response:', response.data);
      if (response.data.success) {
        setRealMetrics(response.data.metrics);
        console.log('Real metrics set:', response.data.metrics);
      }
    } catch (error) {
      console.error('Error fetching real metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatRevenue = (amount) => {
    if (!amount) return '$284k';
    if (amount >= 1000000) {
      return `$${(amount / 1000000).toFixed(1)}M`;
    } else if (amount >= 1000) {
      return `$${Math.round(amount / 1000)}k`;
    }
    return `$${amount.toFixed(0)}`;
  };

  const metrics = {
    active_shipments: realMetrics?.total_bookings || 178,
    active_shipments_change: '+12%',
    pending_quotes: realMetrics?.pending_quotes || 18,
    pending_quotes_change: realMetrics?.conversion_rate || '+8%',
    in_transit: realMetrics?.in_transit_containers || realMetrics?.total_containers || 156,
    in_transit_change: '+8%',
    revenue: formatRevenue(realMetrics?.revenue || realMetrics?.total_profit),
    revenue_change: '+15%'
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Select date';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Select date';
      return date.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch {
      return 'Select date';
    }
  };

  const headingText = 'Logistics Overview';


  if (loading) {
    return (
      <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
        <div className="animate-pulse p-6 space-y-4">
          <div className="h-6 bg-slate-200 dark:bg-slate-800 rounded w-1/3"></div>
          <div className="h-32 bg-slate-200 dark:bg-slate-800 rounded"></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-40 bg-slate-200 dark:bg-slate-800 rounded-xl"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <section className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="px-6 py-5 border-b border-slate-200 dark:border-slate-800 flex flex-wrap items-center justify-between gap-4">
        <div className="min-w-0">
          <div className="inline-flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-[0.2em] text-blue-600 dark:text-blue-400">
            <Sparkles className="w-3 h-3" strokeWidth={2.5} />
            {/* {companyId === 2 ? "Infox AI-Driven Summary" : "Shipez AI-Driven Summary"} */}
            AI-Driven Summary
          </div>
          <motion.h2
            initial={{ opacity: 0, y: 8, filter: 'blur(6px)' }}
            animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
            className="mt-1.5 text-[22px] font-bold text-slate-900 dark:text-white tracking-tight leading-tight"
          >
            <motion.span
              className="inline-block overflow-hidden whitespace-nowrap align-bottom"
              initial={{ width: '0ch' }}
              animate={{ width: ['0ch', `${headingText.length}ch`, `${headingText.length}ch`, '0ch'] }}
              transition={{
                duration: 4.8,
                times: [0, 0.22, 0.9, 1],
                ease: ['linear', 'linear', 'easeInOut'],
                repeat: Infinity,
              }}
            >
              {headingText}
            </motion.span>
          </motion.h2>
          <div className="mt-1 text-[12.5px] text-slate-500 dark:text-slate-400">
            Operational health, fleet status, and key revenue metrics at a glance.
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {onAISummaryClick && (
            <button
              onClick={onAISummaryClick}
              className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white text-[13px] font-semibold shadow-md transition-all hover:shadow-lg dark:shadow-blue-900/20"
              data-testid="ai-summary-button"
            >
              <Sparkles className="w-4 h-4" /> AI Summary
            </button>
          )}

          <div className="relative inline-flex items-center gap-2 h-10 px-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-[12.5px] hover:border-slate-300 dark:hover:border-slate-600 transition-colors cursor-pointer">
            <Calendar className="w-4 h-4 text-slate-500 dark:text-slate-400" />
            <span className="text-slate-500 dark:text-slate-400">From</span>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            />
            <span className="font-medium text-slate-900 dark:text-white tabular-nums">{formatDate(fromDate)}</span>
            <span className="text-slate-300 dark:text-slate-600">—</span>
            <span className="text-slate-500 dark:text-slate-400">To</span>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            />
            <span className="font-medium text-slate-900 dark:text-white tabular-nums">{formatDate(toDate)}</span>
            <ChevronDown className="w-3.5 h-3.5 text-slate-400 dark:text-slate-500" />
          </div>
        </div>
      </div>

      {/* AI insight strip - Improved dark theme */}
      <div className="px-6 pt-5">
        <div className="relative rounded-xl bg-gradient-to-r from-blue-50 to-blue-50/40 dark:from-blue-800/30 dark:to-slate-800/80 border border-blue-100 dark:border-slate-700 px-5 py-4 flex items-start gap-4 transition-all">
          <span className="absolute left-0 top-3 bottom-3 w-[3px] rounded-r bg-gradient-to-b from-blue-600 to-blue-500 dark:from-blue-500 dark:to-blue-400" />
          <div className="shrink-0 w-9 h-9 rounded-lg bg-gradient-to-br from-blue-600 to-blue-500 dark:from-blue-500 dark:to-blue-600 text-white flex items-center justify-center shadow-md dark:shadow-blue-900/20">
            <Sparkles className="w-4 h-4" strokeWidth={2.4} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-blue-700 dark:text-blue-400">
              AI Insight
            </div>
            <p className="mt-0.5 text-[13px] leading-relaxed text-slate-700 dark:text-slate-300">
              {summary?.summary || "Loading AI summary..."}
            </p>
          </div>
        </div>
      </div>

      {/* KPI cards */}
      <div className="p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          icon={Package}
          label="POs on Water"
          value={metrics.active_shipments}
          sub="vessels in transit"
          trend={metrics.active_shipments_change}
          accentKey="emerald"
        />
        <KpiCard
          icon={FileText}
          label="Pending RFQ"
          value={metrics.pending_quotes}
          sub="awaiting quote"
          trend={metrics.pending_quotes_change}
          accentKey="amber"
        />
        <KpiCard
          icon={Container}
          label="Fully Loaded POs"
          value={metrics.in_transit}
          sub="this month"
          trend={metrics.in_transit_change}
          accentKey="violet"
        />
        <KpiCard
          icon={DollarSign}
          label="Revenue"
          value={metrics.revenue}
          sub="this period"
          trend={metrics.revenue_change}
          accentKey="blue"
        />
      </div>

      {/* Add keyframes animation for spin */}
      <style jsx>{`
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        .animate-\\[spin_7s_linear_infinite\\] {
          animation: spin 7s linear infinite;
        }
      `}</style>
    </section>
  );
};

export default AIOverviewPanel;